import { atom, getDefaultStore, useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { queueBusyChatsAtom } from './queue'
import { turnErrorsAtom } from './turn-error'

/**
 * Per-chat turn status (engine phase 4, HIG-5972; design:
 * docs/research/chat-engine-design-no-effect.md "How this fixes render
 * inconsistency" §2). One status per chat derived from the engine's actual
 * turn lifecycle, replacing the global `isChatLoading` boolean and the
 * module-level streaming maps that used to disagree with each other:
 *
 * - `submitting` — a turn exists (POST/connect in flight) but its stream
 *   isn't delivering yet
 * - `streaming`  — the turn's SSE stream is open and folding events
 * - `draining`   — no live stream, but queued submissions are still draining
 *   (between-turns handoff)
 * - `failed`     — the chat's last turn settled on the Err channel
 *   (`turnErrorsAtom` holds the failure)
 * - `idle`       — none of the above
 *
 * Transitions are driven by the turn registry's owners through
 * `EngineDeps.streaming` (begin/markStreaming/end); this store holds the
 * render-facing state those transitions produce.
 */
export type TurnStatus = 'idle' | LiveTurnPhase | 'draining' | 'failed'
export type LiveTurnPhase = 'submitting' | 'streaming'

export type LiveTurn = {
  /**
   * The id the turn was first registered under (a temp id for new chats) —
   * stable across the temp→real id swap so progress cells keyed by it never
   * remount mid-turn.
   */
  entryId: string
  /** The chat's current conversation id. */
  chatId: string
  phase: LiveTurnPhase
  /** Sidebar display title; untitled turns don't render progress cells. */
  title?: string
  startTime?: number
}

/** A recently finished turn, kept so progress cells and toasts can show completion. */
export type CompletedTurn = {
  title: string
  completedAt: number
  durationMs: number | null
}

export type TurnStatusState = {
  /** Live turns keyed by entryId. */
  live: Record<string, LiveTurn>
  /** Completions keyed by the conversation id current at completion time. */
  completed: Record<string, CompletedTurn>
}

export const emptyTurnStatusState: TurnStatusState = { live: {}, completed: {} }

export const turnStatusStateAtom = atom<TurnStatusState>(emptyTurnStatusState)

export function findLiveTurn(state: TurnStatusState, chatId: string): LiveTurn | undefined {
  // Fast path: turns that never had their id swapped are keyed by it.
  const direct = state.live[chatId]
  if (direct?.chatId === chatId) return direct
  return Object.values(state.live).find((turn) => turn.chatId === chatId)
}

export function beginTurnState(
  state: TurnStatusState,
  chatId: string,
  opts: { title?: string; startTime?: number } | undefined,
  now: number,
): TurnStatusState {
  const existing = findLiveTurn(state, chatId)
  if (existing) {
    const updated: LiveTurn = {
      ...existing,
      phase: 'submitting',
      ...(opts?.title !== undefined ? { title: opts.title } : {}),
      ...(opts?.startTime !== undefined ? { startTime: opts.startTime } : {}),
    }
    return { ...state, live: { ...state.live, [existing.entryId]: updated } }
  }
  const turn: LiveTurn = {
    entryId: chatId,
    chatId,
    phase: 'submitting',
    ...(opts?.title !== undefined ? { title: opts.title } : {}),
    startTime: opts?.startTime ?? now,
  }
  // A fresh turn supersedes the chat's previous completion record.
  const completed = { ...state.completed }
  delete completed[chatId]
  return { live: { ...state.live, [chatId]: turn }, completed }
}

export function markTurnStreamingState(state: TurnStatusState, chatId: string, now: number): TurnStatusState {
  const existing = findLiveTurn(state, chatId)
  if (!existing) {
    const begun = beginTurnState(state, chatId, undefined, now)
    return markTurnStreamingState(begun, chatId, now)
  }
  if (existing.phase === 'streaming') return state
  return { ...state, live: { ...state.live, [existing.entryId]: { ...existing, phase: 'streaming' } } }
}

export function endTurnState(state: TurnStatusState, chatId: string, completedAt: number): TurnStatusState {
  const existing = findLiveTurn(state, chatId)
  if (!existing) return state
  const live = { ...state.live }
  delete live[existing.entryId]
  if (existing.title === undefined) return { ...state, live }
  const completion: CompletedTurn = {
    title: existing.title,
    completedAt,
    durationMs: existing.startTime !== undefined ? completedAt - existing.startTime : null,
  }
  return { live, completed: { ...state.completed, [chatId]: completion } }
}

export function updateTurnChatIdState(state: TurnStatusState, oldChatId: string, newChatId: string): TurnStatusState {
  const existing = findLiveTurn(state, oldChatId)
  if (!existing) return state
  return { ...state, live: { ...state.live, [existing.entryId]: { ...existing, chatId: newChatId } } }
}

export function removeCompletedTurnState(state: TurnStatusState, chatId: string): TurnStatusState {
  if (!(chatId in state.completed)) return state
  const completed = { ...state.completed }
  delete completed[chatId]
  return { ...state, completed }
}

export function deriveTurnStatus(live: LiveTurn | undefined, queueBusy: boolean, hasError: boolean): TurnStatus {
  if (live) return live.phase
  if (queueBusy) return 'draining'
  if (hasError) return 'failed'
  return 'idle'
}

// ---------------------------------------------------------------------------
// Store-bound writers. Non-React lifecycle code (the engine deps, the submit
// flow's finally, the verify path) drives these directly, same pattern as
// queue.ts.
// ---------------------------------------------------------------------------

/**
 * A turn now exists for the chat — status becomes `submitting`. Seeds the
 * sidebar title and start time (explicit start time overrides, e.g. the
 * verify path restoring the backend's turn start; otherwise an existing
 * turn's start time is kept).
 */
export function beginTurn(chatId: string, opts?: { title?: string; startTime?: number }): void {
  getDefaultStore().set(turnStatusStateAtom, (prev) => beginTurnState(prev, chatId, opts, Date.now()))
}

/** The turn's stream is open and delivering — status becomes `streaming`. */
export function markTurnStreaming(chatId: string): void {
  getDefaultStore().set(turnStatusStateAtom, (prev) => markTurnStreamingState(prev, chatId, Date.now()))
}

/** The turn settled (completed or failed) — records a completion for titled turns. */
export function endTurn(chatId: string): void {
  getDefaultStore().set(turnStatusStateAtom, (prev) => endTurnState(prev, chatId, Date.now()))
}

/** Follow a temp→real chat-id rename from the `metadata` event. */
export function updateTurnChatId(oldChatId: string, newChatId: string): void {
  getDefaultStore().set(turnStatusStateAtom, (prev) => updateTurnChatIdState(prev, oldChatId, newChatId))
}

/** Dismiss a completion (cell clicked, chat reopened, toast resumed). */
export function removeCompletedTurn(chatId: string): void {
  getDefaultStore().set(turnStatusStateAtom, (prev) => removeCompletedTurnState(prev, chatId))
}

/** Whether the chat has a live turn (submitting or streaming). */
export function isTurnLive(chatId: string): boolean {
  return findLiveTurn(getDefaultStore().get(turnStatusStateAtom), chatId) !== undefined
}

export type TurnEndOutcome = 'ended' | 'timed-out'

/** Resolves once the chat has no live turn, or after `timeoutMs` — whichever comes first. */
export function waitForTurnEnd(chatId: string, timeoutMs: number): Promise<TurnEndOutcome> {
  if (!isTurnLive(chatId)) return Promise.resolve('ended')
  return new Promise((resolve) => {
    const finish = (outcome: TurnEndOutcome) => {
      clearTimeout(timer)
      unsubscribe()
      resolve(outcome)
    }
    const timer = setTimeout(() => finish('timed-out'), timeoutMs)
    const unsubscribe = getDefaultStore().sub(turnStatusStateAtom, () => {
      if (!isTurnLive(chatId)) finish('ended')
    })
  })
}

/**
 * Whether a stream currently owns the chat's turn in the engine's registry.
 * Differs from `isTurnLive`: a store-level `beginTurn` pre-flip (e.g. the
 * staged-action resume path showing the chat busy before subscribing) makes a
 * chat live without any stream owning it, so callers that need "does another
 * stream own this chat" — as opposed to "should this chat render busy" — read
 * this. Facade over the registry: consumers outside chat/ must import it from
 * here, never from `chat/engine/turn-registry` (enforced by the eslint
 * no-restricted-imports boundary).
 */
export { isTurnRegistered } from '../engine/turn-registry'

export function getTurnStartTime(chatId: string): number | null {
  return findLiveTurn(getDefaultStore().get(turnStatusStateAtom), chatId)?.startTime ?? null
}

// ---------------------------------------------------------------------------
// Render-facing reads
// ---------------------------------------------------------------------------

/**
 * The chat's turn status. The canonical per-chat read — behavior keyed to
 * "this chat's turn" (loaders, previews, input gating) derives from this,
 * never from a global boolean (HIG-5962/R9).
 */
export function useTurnStatus(chatId: string | undefined): TurnStatus {
  const statusAtom = useMemo(
    () =>
      atom((get) => {
        if (!chatId) return 'idle' as TurnStatus
        return deriveTurnStatus(
          findLiveTurn(get(turnStatusStateAtom), chatId),
          get(queueBusyChatsAtom).has(chatId),
          chatId in get(turnErrorsAtom),
        )
      }),
    [chatId],
  )
  return useAtomValue(statusAtom)
}

/**
 * Whether the chat has a live turn (submitting or streaming). Subscribes to
 * the turn state only — not the queue or error atoms — so the message tree's
 * hot path re-renders only when liveness actually changes.
 */
export function useIsTurnLive(chatId: string | undefined): boolean {
  const liveAtom = useMemo(
    () => atom((get) => (chatId !== undefined ? findLiveTurn(get(turnStatusStateAtom), chatId) !== undefined : false)),
    [chatId],
  )
  return useAtomValue(liveAtom)
}

/** One row of the sidebar's in-flight/just-finished progress cells. */
export type ActiveChatEntry = {
  entryId: string
  id: string
  title: string
  status: 'streaming' | 'completed'
  startTime: number | null
  completedAt?: number
  durationMs?: number | null
}

export function buildActiveChatEntries(state: TurnStatusState): Array<ActiveChatEntry> {
  const entries: Array<ActiveChatEntry> = []
  for (const turn of Object.values(state.live)) {
    if (turn.title === undefined) continue
    entries.push({
      entryId: turn.entryId,
      id: turn.chatId,
      title: turn.title,
      status: 'streaming',
      startTime: turn.startTime ?? null,
    })
  }
  for (const [id, completion] of Object.entries(state.completed)) {
    entries.push({
      entryId: id,
      id,
      title: completion.title,
      status: 'completed',
      startTime: null,
      completedAt: completion.completedAt,
      durationMs: completion.durationMs,
    })
  }
  return entries
}

const activeChatEntriesAtom = atom((get) => buildActiveChatEntries(get(turnStatusStateAtom)))

/** All in-flight turns with titles plus recently completed turns, for progress cells. */
export function useActiveAndCompletedTurns(): Array<ActiveChatEntry> {
  return useAtomValue(activeChatEntriesAtom)
}

/** Non-reactive snapshot of what useActiveAndCompletedTurns returns. */
export function getActiveChatEntries(): Array<ActiveChatEntry> {
  return getDefaultStore().get(activeChatEntriesAtom)
}

// Unfiltered live-turn ids. `buildActiveChatEntries` drops title-less turns,
// which is exactly what a detached-completion watcher must not miss — a chat
// whose subscribe was aborted before a title landed is still live. The cache
// keeps the array reference stable across unrelated state changes (title
// backfills, phase flips) because the list drives a polling effect: a fresh
// array on every write would restart the interval and re-hit the backend.
let _liveChatIdsCache: Array<string> = []

const liveChatIdsAtom = atom((get) => {
  const next = Array.from(new Set(Object.values(get(turnStatusStateAtom).live).map((turn) => turn.chatId)))
  if (next.length !== _liveChatIdsCache.length || next.some((id, index) => id !== _liveChatIdsCache[index])) {
    _liveChatIdsCache = next
  }
  return _liveChatIdsCache
})

/** Non-reactive list of every live turn's current conversation id, title-less turns included. */
export function getActiveStreamingChatIds(): Array<string> {
  return getDefaultStore().get(liveChatIdsAtom)
}

/** Reactive counterpart of `getActiveStreamingChatIds`. */
export function useActiveStreamingChatIds(): Array<string> {
  return useAtomValue(liveChatIdsAtom)
}
